import React, { useState } from 'react';
import styled from 'styled-components';
import { Search, QrCode, UserPlus, ArrowRight, Calendar, Phone, Clock, Keyboard } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DateInputDDMMYYYY from '../../components/DateInputDDMMYYYY/DateInputDDMMYYYY';
import CameraQrScanner from '../../components/CameraQrScanner';

const Page = styled.div`max-width: 1100px; margin: 0 auto;`;
const Card = styled.div`background: #fff; border-radius: 12px; padding: 1.75rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 1.5rem;`;
const Title = styled.h2`font-size: 1.35rem; font-weight: 700; color: #1e293b; margin: 0 0 0.35rem; display: flex; align-items: center; gap: 0.5rem;`;
const Sub = styled.p`color: #64748b; font-size: 0.9rem; margin: 0 0 1.5rem;`;
const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  align-items: start;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;
const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background: #e5e7eb;
  @media (max-width: 800px) {
    width: 100%;
    height: 1px;
  }
`;
const Panel = styled.div`display: flex; flex-direction: column; gap: 0.9rem;`;
const PanelLabel = styled.h3`font-size: 0.85rem; font-weight: 600; color: #64748b; margin: 0; display: flex; align-items: center; gap: 0.4rem; text-transform: uppercase; letter-spacing: 0.03em;`;
const Row = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; @media (max-width: 640px){ grid-template-columns: 1fr; }`;
const Field = styled.div`display: flex; flex-direction: column; gap: 0.4rem;`;
const Label = styled.label`font-size: 0.85rem; font-weight: 500; color: #475569;`;
const Input = styled.input`padding: 0.7rem 0.85rem; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 0.95rem; background: #fff; &:focus{ outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102,126,234,0.12);}`;
const Actions = styled.div`display: flex; gap: 0.75rem; margin-top: 0.25rem; flex-wrap: wrap;`;
const ManualToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: none;
  padding: 0.15rem 0;
  color: #667eea;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  width: fit-content;
  &:hover { text-decoration: underline; }
`;
const PrimaryBtn = styled.button`padding: 0.7rem 1.25rem; background: linear-gradient(135deg,#667eea,#764ba2); color: #fff; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; &:disabled{ opacity: .6; cursor: not-allowed;}`;
const GhostBtn = styled.button`padding: 0.7rem 1.25rem; background: #fff; color: #475569; border: 1px solid #e5e7eb; border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; &:hover{ border-color: #cbd5e1;}`;
const BottomActions = styled.div`display: flex; gap: 0.75rem; margin-top: 1.5rem; padding-top: 1.25rem; border-top: 1px solid #f1f5f9; flex-wrap: wrap;`;
const Err = styled.div`background: #fee2e2; color: #b91c1c; padding: 0.7rem 0.9rem; border-radius: 8px; font-size: 0.85rem; margin-top: 1rem;`;
const ClientCard = styled.div`border: 1px solid #e5e7eb; border-radius: 10px; padding: 1rem 1.25rem; margin-bottom: 0.75rem; display: flex; justify-content: space-between; align-items: center; gap: 1rem; transition: all .15s; &:hover{ border-color: #667eea; box-shadow: 0 2px 8px rgba(102,126,234,0.12);}`;
const CName = styled.div`font-weight: 700; color: #1e293b; font-size: 1rem;`;
const CMeta = styled.div`display: flex; gap: 1.25rem; flex-wrap: wrap; color: #64748b; font-size: 0.82rem; margin-top: 0.3rem;`;
const MetaItem = styled.span`display: inline-flex; align-items: center; gap: 0.3rem;`;
const NoRes = styled.div`text-align: center; color: #94a3b8; padding: 2rem; font-style: italic;`;

const fmtDate = (v) => {
  if (!v) return null;
  try { const d = new Date(v); if (isNaN(d.getTime())) return String(v); return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return String(v); }
};

const ReturningClient = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [qr, setQr] = useState('');
  const [showManualQr, setShowManualQr] = useState(false);
  const [results, setResults] = useState(null); // null = not searched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = () => { const tk = localStorage.getItem('token'); return tk ? { Authorization: `Bearer ${tk}` } : {}; };

  const searchByName = async () => {
    setError('');
    if (!name.trim() && !birthday.trim()) { setError('Enter a name or a birthday to search.'); return; }
    setLoading(true);
    try {
      const res = await axios.get('/api/visitors/clients', { headers: headers(), params: { search: name.trim(), _: Date.now() } });
      let list = Array.isArray(res.data) ? res.data : (res.data?.clients || []);
      const bday = birthday.trim();
      if (bday) {
        list = list.filter((c) => {
          const b = c.birthday ? String(c.birthday) : '';
          if (!b) return false;
          if (b.includes(bday)) return true;
          const iso = (() => { try { return new Date(c.birthday).toISOString().split('T')[0]; } catch { return ''; } })();
          return iso && (iso === bday || iso.includes(bday));
        });
      }
      setResults(list);
    } catch (e) {
      setError(e.response?.data?.message || 'Search failed. Please try again.');
    } finally { setLoading(false); }
  };

  // A client QR encodes a CL-XXXX-XXXX code; an order QR/barcode encodes a
  // barcode or order number. Try the client-code lookup first (cheap 400/404
  // on a non-match) and fall back to the order lookup, so one scan box
  // handles both without the operator picking a mode.
  const lookupByQr = async (raw) => {
    setError('');
    const code = (raw ?? qr).trim();
    if (!code) { setError('Scan or type a client or order code.'); return; }
    setLoading(true);
    try {
      try {
        const res = await axios.get(`/api/visitors/by-client-code/${encodeURIComponent(code)}`, { headers: headers() });
        const c = res.data || {};
        await proceedWithClient(c.clientKey || c.visitorId, { firstName: c.firstName, lastName: c.lastName, phone: c.phone });
        return;
      } catch (e) {
        if (e.response?.status !== 404 && e.response?.status !== 400) throw e;
        // Not a client code (or no client matches it) — fall through to order lookup.
      }

      const res = await axios.get(`/api/orders/barcode/${encodeURIComponent(code)}`, { headers: headers() });
      const order = res.data || {};
      const visitorId = order.visitor?._id || order.visitor?.id || order.visitorId || order.visitor_id
        || (typeof order.visitor === 'string' ? order.visitor : null);
      const phone = order.prescription?.phoneNumber || order.visitor?.phone || null;
      const identifier = visitorId || phone;
      if (!identifier) { setError('This order has no linked client on file.'); return; }
      await proceedWithClient(identifier, {
        firstName: order.prescription?.patientName?.split(' ')?.[0] || '',
        lastName: order.prescription?.patientName?.split(' ')?.slice(1).join(' ') || '',
        phone, gender: order.visitor?.gender, ageGroup: order.visitor?.ageGroup,
        birthday: order.prescription?.patientBirthday, age: order.prescription?.patientAge,
      });
    } catch (e) {
      setError(e.response?.status === 404 ? 'No client or order found for that code.' : (e.response?.data?.message || 'Lookup failed.'));
    } finally { setLoading(false); }
  };

  const handleScanDecode = (text) => {
    setQr(text);
    // Reveal the code field too, so a failed match leaves the scanned text
    // visible to edit/retry instead of disappearing behind the toggle.
    setShowManualQr(true);
    lookupByQr(text);
  };

  // Mirror PrescriptionForm.selectExistingClientMatch: prefill identity + past RX
  // via the same sessionStorage keys, then continue into the normal flow.
  const proceedWithClient = async (identifier, partial = {}) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/visitors/client-details/${encodeURIComponent(String(identifier))}`, { headers: headers() });
      const details = res.data || {};
      const profile = details.profile || {};
      const firstName = profile.firstName || partial.firstName || '';
      const lastName = profile.lastName || partial.lastName || '';
      const phone = profile.phone || partial.phone || '';
      const gender = profile.gender || partial.gender || '';
      const ageGroup = profile.ageGroup || partial.ageGroup || '';
      const patientBirthday = profile.patientBirthday || partial.birthday || '';
      const patientAge = profile.patientAge || partial.age || '';
      const previousClientId = profile._id || profile.id || partial.visitorId || partial._id || identifier;
      const latestPrev = Array.isArray(details.prescriptions) ? details.prescriptions[0] : null;
      // Carry over everything on file — address + occupation come from the most
      // recent prescription (the fullest record) with profile as fallback.
      const address = latestPrev?.address || profile.address || partial.address || '';
      const occupation = latestPrev?.occupation || profile.occupation || '';

      const visitorData = { gender, ageGroup, firstName, lastName, phone, email: profile.email || partial.email || '', patientBirthday, patientAge, address, occupation, previousClientId };
      sessionStorage.setItem('visitorData', JSON.stringify(visitorData));
      sessionStorage.setItem('returningClientData', JSON.stringify(visitorData));
      if (latestPrev) {
        sessionStorage.setItem('previousPrescription', JSON.stringify({ clientId: previousClientId, prescription: latestPrev }));
      } else {
        sessionStorage.removeItem('previousPrescription');
      }
      // Returning client: identity is known, so skip the visit step and go
      // straight to the RX page. The prescription page creates the visitor from
      // visitorData on save (store derived from the salesperson).
      navigate('/prescription');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load this client. Please try again.');
      setLoading(false);
    }
  };

  const startNewClient = () => {
    // Fresh blank flow — clear any returning-client prefill.
    ['returningClientData', 'previousPrescription'].forEach((k) => sessionStorage.removeItem(k));
    navigate('/visitor-entry');
  };

  return (
    <Page>
      <Card>
        <Title><Search size={22} /> {t('returningClient.title', 'Find a returning client')}</Title>
        <Sub>{t('returningClient.subtitle', 'Look up an existing client to start a new visit with their details and past prescription — or start a brand-new client.')}</Sub>

        <SplitGrid>
          <Panel>
            <PanelLabel><Search size={13} /> {t('returningClient.nameBirthday')}</PanelLabel>
            <Row>
              <Field>
                <Label>{t('returningClient.nameOrPhone')}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('returningClient.eGAhmedOr0555')}
                  onKeyDown={(e) => { if (e.key === 'Enter') searchByName(); }} />
              </Field>
              <Field>
                <Label>{t('returningClient.birthdayOptional')}</Label>
                <DateInputDDMMYYYY value={birthday} onChange={setBirthday} placeholder={t('returningClient.ddMmYyyy')} />
              </Field>
            </Row>
            <Actions>
              <PrimaryBtn onClick={searchByName} disabled={loading}><Search size={16} /> {loading ? t('returningClient.searching') : t('returningClient.search')}</PrimaryBtn>
            </Actions>
          </Panel>

          <Divider />

          <Panel>
            <PanelLabel><QrCode size={13} /> {t('returningClient.scanClientOrder')}</PanelLabel>
            <CameraQrScanner
              onDecode={handleScanDecode}
              buttonLabel="Scan QR"
              helpText="Scan a client card or order QR/barcode."
            />
            {showManualQr ? (
              <>
                <Field>
                  <Label>{t('returningClient.clientOrderCode')}</Label>
                  <Input value={qr} onChange={(e) => setQr(e.target.value)} placeholder={t('returningClient.eGClA1b2C3d4')}
                    autoFocus onKeyDown={(e) => { if (e.key === 'Enter') lookupByQr(); }} />
                </Field>
                <Actions>
                  <PrimaryBtn onClick={() => lookupByQr()} disabled={loading}><QrCode size={16} /> {loading ? t('returningClient.lookingUp') : t('returningClient.findClient')}</PrimaryBtn>
                </Actions>
              </>
            ) : (
              <ManualToggle type="button" onClick={() => setShowManualQr(true)}>
                <Keyboard size={14} /> {t('returningClient.orTypeACodeManually')}
              </ManualToggle>
            )}
          </Panel>
        </SplitGrid>

        {error && <Err>{error}</Err>}

        <BottomActions>
          <GhostBtn onClick={startNewClient}><UserPlus size={16} /> {t('returningClient.newClientSkip')}</GhostBtn>
        </BottomActions>
      </Card>

      {results !== null && (
        <Card>
          <Title style={{ fontSize: '1.05rem' }}>{results.length} match{results.length === 1 ? '' : 'es'}</Title>
          {results.length === 0 ? (
            <NoRes>{t('returningClient.noExistingClientFoundUse')}</NoRes>
          ) : results.map((c, i) => {
            const full = `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unnamed client';
            return (
              <ClientCard key={c.clientKey || c.visitorId || c._id || i}>
                <div>
                  <CName>{full}</CName>
                  <CMeta>
                    {c.phone && <MetaItem><Phone size={14} /> {c.phone}</MetaItem>}
                    {c.birthday && <MetaItem><Calendar size={14} /> {fmtDate(c.birthday)}</MetaItem>}
                    {c.lastVisit && <MetaItem><Clock size={14} /> {t('returningClient.lastVisit')} {fmtDate(c.lastVisit)}</MetaItem>}
                    {c.store?.name && <MetaItem>{c.store.name}</MetaItem>}
                  </CMeta>
                </div>
                <PrimaryBtn onClick={() => proceedWithClient(c.clientKey || c.visitorId || c._id || c.phone, c)} disabled={loading}>
                  {t('returningClient.startNewVisit')} <ArrowRight size={16} />
                </PrimaryBtn>
              </ClientCard>
            );
          })}
        </Card>
      )}
    </Page>
  );
};

export default ReturningClient;
